/**
 * Quick add employee (WO create: employee selection).
 *
 * Only what the work order needs: full name, passport number, Emirates ID, and optional Passport / Emirates ID files.
 * A file is read by OCR and fills the empty fields. "Create employee" creates the customer's employee in Thaklis, then
 * uploads the given files to it with their extracted number / issue / expiry dates, and hands the new employee back.
 */

import React, { useState } from 'react';
import {
  Alert, Backdrop, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';

const PASSPORT = 'PASSPORT_COPY';
const EMIRATES_ID = 'EMIRATES_ID'; // the TC's type code (not EID_COPY), so it is auto-filled on WO create and its number is read

const clean = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  return (!s || s.toLowerCase() === 'null') ? '' : s;
};

// The WO create page's sizes (the dialog renders outside the page, so it does not inherit them)
const COMPACT_SX = {
  '& .MuiInputBase-root': { height: 36 },
  '& .MuiInputBase-input': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
  '& .MuiButton-root': { fontSize: '0.8125rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.7rem' },
};

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

const QuickEmployeeDialog = ({
  open, onClose, customer, initialName = '', initialPassportNumber = '', initialEmiratesId = '', onCreated, compact = false,
}) => {
  const [form, setForm] = useState({ name: initialName, passportNumber: initialPassportNumber, emiratesId: initialEmiratesId });
  // { [docType]: { file, extracting, documentNumber, issueDate, expiryDate } }
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (open) {
      setForm({ name: initialName, passportNumber: initialPassportNumber, emiratesId: initialEmiratesId });
      setFiles({});
      setError(null);
    }
  }, [open, initialName, initialPassportNumber, initialEmiratesId]);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const pickFile = async (docType, file) => {
    if (!file) return;
    setFiles((p) => ({ ...p, [docType]: { file, extracting: true } }));
    try {
      const res = await ApiService.extractDocument(file, 'GENERAL', docType);
      const x = res?.data?.data || {};
      setFiles((p) => ({
        ...p,
        [docType]: {
          file,
          extracting: false,
          documentNumber: resolveExtractedDocNumber(x, docType) || null,
          issueDate: clean(x.issueDate) || null,
          expiryDate: clean(x.expiryDate) || null,
        },
      }));
      // fill only what is still empty, so a typed value is never overwritten
      setForm((p) => ({
        name: p.name.trim() ? p.name : clean(x.fullName),
        passportNumber: p.passportNumber.trim() ? p.passportNumber : clean(x.passportNumber),
        emiratesId: p.emiratesId.trim() ? p.emiratesId : clean(x.emiratesIdNumber),
      }));
      if (res?.data?.success === false || x._extractionSkipped) {
        setError(x._extractionSkippedReason || 'Automatic reading was skipped — enter the fields yourself.');
      }
    } catch (err) {
      setFiles((p) => ({ ...p, [docType]: { file, extracting: false } }));
      setError('The file could not be read — enter the fields yourself.');
    }
  };

  const extracting = Object.values(files).some((f) => f.extracting);
  const canCreate = form.name.trim() && (form.passportNumber.trim() || form.emiratesId.trim()) && !extracting && !saving;

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await ApiService.quickCreateCustomerEmployee({
        customer,
        name: form.name.trim(),
        passportNumber: form.passportNumber.trim(),
        emiratesId: form.emiratesId.trim(),
      });
      const created = res?.data?.data;
      if (!created?.uid) throw new Error(res?.data?.message || 'No employee was returned');
      const failed = [];
      await Promise.all(Object.entries(files).map(async ([docType, f]) => {
        try {
          await ApiService.uploadEmployeeDocuments(created.uid, [f.file], docType, {
            documentNumber: f.documentNumber, issueDate: f.issueDate, expiryDate: f.expiryDate,
          }, created.segment);
        } catch (e) {
          failed.push(docType === PASSPORT ? 'passport' : 'Emirates ID');
        }
      }));
      onCreated({
        uid: created.uid,
        segment: created.segment,
        name: created.name || form.name.trim(),
        passportNumber: created.passport_number || form.passportNumber.trim(),
        emiratesId: created.emirates_id || form.emiratesId.trim(),
      }, failed);
    } catch (err) {
      setError(serverMessage(err, 'The employee could not be created'));
    } finally {
      setSaving(false);
    }
  };

  const fileButton = (docType, label) => {
    const f = files[docType];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 1 }}>
        <Button component="label" size="small" variant="outlined" startIcon={<UploadFile />} disabled={saving}
          sx={{ textTransform: 'none', flexShrink: 0 }}>
          {f ? `Change ${label}` : `Upload ${label}`}
          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => { pickFile(docType, e.target.files?.[0]); e.target.value = ''; }} />
        </Button>
        <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 0 }}>
          {f ? f.file.name : 'Optional — read to fill the fields'}
        </Typography>
        {f?.extracting && <CircularProgress size={14} />}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth
      PaperProps={compact ? { sx: COMPACT_SX } : undefined}>
      <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Quick add employee</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          {fileButton(PASSPORT, 'passport')}
          {fileButton(EMIRATES_ID, 'Emirates ID')}
          <TextField label="Full name" size="small" value={form.name} onChange={set('name')} required autoFocus />
          <TextField label="Passport number" size="small" value={form.passportNumber} onChange={set('passportNumber')} />
          <TextField label="Emirates ID" size="small" value={form.emiratesId} onChange={set('emiratesId')}
            helperText="Enter the passport number or the Emirates ID" />
          {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} size={compact ? 'small' : 'medium'} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={!canCreate} size={compact ? 'small' : 'medium'} sx={{ textTransform: 'none' }}>
          {saving ? 'Creating…' : 'Create employee'}
        </Button>
      </DialogActions>
      {/* Blocks the page (dialog included) until the employee is created and its files are uploaded */}
      <Backdrop open={saving} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff', flexDirection: 'column', gap: 1 }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ fontSize: '0.8125rem' }}>Creating the employee…</Typography>
      </Backdrop>
    </Dialog>
  );
};

export default QuickEmployeeDialog;
